import React, { Component } from 'react';

import classNames from 'classnames';
import { InputBlock } from 'components/input';
import { Loader } from 'components/loader';
import { Panel } from 'components/panel';
import { Radiolist } from 'components/radiolist';
import { I18n, translate } from 'components/translate';
import Currency from 'helpers/Currency';
import { isObject } from 'helpers/Object';
import groupBy from 'lodash/groupBy';
import intersectionBy from 'lodash/intersectionBy';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { ProductService } from 'modules/product';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';

import { ITEM_TYPE, NO_SUBSCRIPTION, PERIODICITIES } from '../constants';

@inject('MembershipStore', 'UiStore')
@translate()
@observer
class OfferRadiolist extends Component {
  // #region setup
  static propTypes = {
    MembershipStore: MobxPropTypes.objectOrObservableObject,
    UiStore: MobxPropTypes.objectOrObservableObject,
    labelValue: PropTypes.string,
    name: PropTypes.string,
    product: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    t: PropTypes.func,
    value: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.object,
      PropTypes.string
    ]),
    onChange: PropTypes.func
  };

  state = {
    offers: [],
    offersPage: 0,
    offersSize: null,
    loading: false
  };

  constructor(props) {
    super(props);
    this.formatItemMobile = this.formatItemMobile.bind(this);
    this.formatItemDesktop = this.formatItemDesktop.bind(this);
  }

  async componentDidMount() {
    await this.fetchData();
  }

  // eslint-disable-next-line
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { product, value } = nextProps;
    const productIsValid = isObject(product);

    if (productIsValid) {
      const productChanged =
        !isEmpty(product) && !isEqual(product, this.props.product);
      const offersChanged = this.isHotlink(product)
        ? value && typeof value === 'string'
        : value && Array.isArray(value);

      if (offersChanged) {
        this.onOffersChange(value, product);
      }
      if (productChanged) {
        this.onProductChange(product);
      }
    }
  }
  // #endregion

  // #region actions
  async fetchData(params = {}) {
    const product = this.productToFetch(params.product);

    if (product) {
      let { offers, offersPage } = this.state;
      let response = null;

      this.setState({ loading: true });

      try {
        response = await ProductService.getOffers({
          id: product.id,
          owner: product.owner,
          page: ++offersPage
        });
        const results = product.owner ? response && response.data : response;

        if (results) {
          offers = offers.concat(results);

          if (Array.isArray(params.offerParam)) {
            offers.map((offer) => {
              const offerFound = params.offerParam.find(
                (item) => item.key === offer.key
              );

              if (offerFound && offerFound.id) {
                offer.id = offerFound.id;
              }

              return offer;
            });
          }

          this.setState(
            { offers, offersPage: response.page, offersSize: response.size },
            () => {
              params.offerParam && this.checkIfOffersExist(params);
              this.checkSingleOffer();
            }
          );
        }
      } catch (error) {
        UiService.setAlertError('default_error');
      } finally {
        this.setState({ loading: false });
      }

      return response;
    }
  }

  productToFetch(productParam) {
    if (productParam) {
      return productParam;
    }

    const { product } = this.props;
    const productIsValid = isObject(product);

    return productIsValid && product;
  }

  async checkIfOffersExist(params) {
    const { value } = this.props;
    const offers = this.getOffersByKey(params.offerParam, params.product);
    const fetchMoreHotlinks =
      this.isHotlink(params.product) && offers[0].key !== params.offerParam;
    const fetchMoreOffers =
      !this.isHotlink(params.product) &&
      offers.length !== params.offerParam.length;

    if (isEmpty(offers) || fetchMoreHotlinks || fetchMoreOffers) {
      await this.fetchData({ params });
    } else if (!isEqual(offers, value)) {
      this.setOffers(offers);
    }
  }

  checkSingleOffer() {
    const { offers } = this.state;

    if (offers.length === 1) {
      const periodicity = offers[0].periodicity;

      this.setOffers({ [periodicity]: offers[0] });
    }
  }
  // #endregion

  // #region getters
  get shouldFetchOffers() {
    if (this.isHotlink()) {
      return false;
    }

    const { offers, offersSize } = this.state;

    return offers.length !== offersSize;
  }

  get offers() {
    let { offers } = this.state;
    const transform = !this.isHotlink()
      ? this.transformOffer.bind(this)
      : this.transformOfferHotlink.bind(this);

    offers = offers.map((offer) => transform(offer));

    return groupBy(offers, 'periodicity');
  }

  get formatItemFn() {
    const { isMobile } = this.props.UiStore;
    let formatFn = null;

    if (this.isHotlink()) {
      formatFn = this.formatItemHotlink;
    } else if (isMobile) {
      formatFn = this.formatItemMobile;
    } else {
      formatFn = this.formatItemDesktop;
    }

    return formatFn;
  }

  isHotlink(productParam) {
    if (productParam) {
      return !productParam.owner;
    }

    const { product } = this.props;

    return isObject(product) && !product.owner;
  }

  getOffersByKey(value, product) {
    const searchParam = this.isHotlink(product)
      ? [value].map((item) => {
          item = { key: item };
          return item;
        })
      : value.map((item) => {
          item.key = item.offerKey;
          return item;
        });

    return intersectionBy(this.state.offers, searchParam, 'key');
  }
  // #endregion

  // #region helpers
  isHeadingItem(item) {
    return Object.values(ITEM_TYPE).includes(item.type);
  }

  transformOffer(offer) {
    if (!this.isHeadingItem(offer)) {
      if (!offer.name) {
        offer.name = !offer.description
          ? this.props.t('general.default')
          : offer.description;
      }

      offer.periodicity = offer.periodicity || NO_SUBSCRIPTION;
    } else if (!offer.name) {
      offer.name = offer.description;
    }

    offer.value = offer.key;
    delete offer.description;

    return offer;
  }

  transformOfferHotlink(offer) {
    if (!offer.name) {
      offer.name = offer.description || offer.url;
    }

    offer.key = offer.url;
    offer.value = offer.url;
    offer.periodicity = NO_SUBSCRIPTION;
    delete offer.description;

    return offer;
  }

  formatItemMobile = (item) => {
    return !this.isHeadingItem(item) ? (
      <div className="radio__custom__label">
        <div>{item.name}</div>
        <div className="secondary">{item.key}</div>
        {item.price && (
          <div className="secondary">
            <I18n label="general.price" />
            {`: ${Currency.formatCurrencySymbol(
              item.price,
              item.currencyCode
            )}`}
          </div>
        )}
      </div>
    ) : null;
  };

  formatItemDesktop = (item) => {
    const priceElement =
      item.type !== ITEM_TYPE.SUBHEADER && item.price ? (
        <span>{`${Currency.formatCurrencySymbol(
          item.price,
          item.currencyCode
        )}`}</span>
      ) : (
        <span>{item.price}</span>
      );

    return (
      <div className="radio__custom__label">
        <span>
          {item.name}
          <br />
          <span className="secondary">{item.key}</span>
        </span>
        {item.price && priceElement}
      </div>
    );
  };

  formatItemHotlink = (item) => {
    return (
      <div className="radio__custom__label__hotlink">
        <div className="offer__label__check">{item.name}</div>

        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="offer-hotlink"
        >
          <span className="text">
            <I18n label="advertisement_editor.hotlink_access" />
          </span>
          <i className="icon icon-arrow-link" />
        </a>
      </div>
    );
  };

  offersHeading(periodicity, amount) {
    let subheader = [];
    const { product, UiStore } = this.props;
    const hideSubheader =
      this.isHotlink() || (UiStore.isMobile && !product.subscription);

    if (hideSubheader) {
      subheader = [];
    } else if (periodicity === NO_SUBSCRIPTION) {
      subheader = [
        {
          type: ITEM_TYPE.SUBHEADER,
          name: 'general.name',
          price: 'general.price'
        }
      ];
    } else {
      subheader = [
        {
          type: ITEM_TYPE.SUBHEADER,
          name: `general.periodicity.${PERIODICITIES[periodicity]}`,
          amount
        }
      ];
    }

    return subheader;
  }

  selectedOption(periodicity) {
    const { value } = this.props;

    const offer = !Array.isArray(value)
      ? value[periodicity]
      : intersectionBy(this.offers[periodicity], value, 'key')[0];

    return offer;
  }
  // #endregion

  // #region handlers
  handleScroll = (event) => {
    const {
      scrollHeight,
      clientHeight,
      scrollTop: scrollPosition
    } = event.nativeEvent.target;
    const maxScroll = scrollHeight - clientHeight;

    if (maxScroll === scrollPosition && this.shouldFetchOffers) {
      this.fetchData();
    }
  };

  onOffersChange(offerParam, product) {
    const offers = this.getOffersByKey(offerParam, product);

    if (!isEmpty(offers)) {
      this.setOffers(offers);
    } else if (this.isHotlink(product) || this.shouldFetchOffers) {
      this.fetchData({ offerParam, product });
    }
  }

  onProductChange(product) {
    if (typeof this.props.product !== 'string') {
      this.setOffers({});
    }

    this.setState(
      {
        offers: [],
        offersPage: 0,
        offersSize: null
      },
      () => this.fetchData({ product })
    );
  }

  onRadiolistChange = (event, periodicity) => {
    const { value: key } = event.target;
    let { value } = this.props;

    if (Array.isArray(value) || !value) {
      value = {};
    }

    const uncheckSubscriptionOffer =
      periodicity !== NO_SUBSCRIPTION &&
      value[periodicity] &&
      value[periodicity].key === key;

    if (uncheckSubscriptionOffer) {
      delete value[periodicity];
    } else {
      value[periodicity] = this.offers[periodicity].find(
        (item) => item.key === key
      );
    }

    this.setOffers(value);
  };

  setOffers(value) {
    const { name, onChange } = this.props;

    if (Array.isArray(value)) {
      value = groupBy(value, 'periodicity');
      Object.keys(value).map((key) => (value[key] = value[key][0]));
    }

    onChange && onChange({ target: { name, value } });
  }
  // #endregion

  render() {
    const { labelValue, product, ...props } = this.props;
    const formatItemFn = this.formatItemFn;
    const offers = this.offers;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return (
      <InputBlock
        {...props}
        labelValue={labelValue}
        className={classNames('offer__radiolist__input', {
          'offer__radiolist--show': product
        })}
      >
        <div className="offer__radiolist">
          <Panel
            header="advertisement_editor.advertisement_offers"
            key="offers__radiolist__panel"
            nopadding
            handleScroll={this.handleScroll.bind(this)}
          >
            {!isEmpty(offers) &&
              Object.keys(offers).map((periodicity) => {
                const selectedOption = that.selectedOption(periodicity);

                return (
                  <Radiolist
                    {...props}
                    formatItem={formatItemFn}
                    key={`radio__list__${periodicity}`}
                    name={`radio__list__${periodicity}`}
                    selectedOption={selectedOption && selectedOption.key}
                    onChange={(event) =>
                      that.onRadiolistChange(event, periodicity)
                    }
                    allowUncheck={
                      selectedOption &&
                      selectedOption.periodicity !== NO_SUBSCRIPTION
                    }
                    items={that
                      .offersHeading(periodicity, offers[periodicity].length)
                      .concat(offers[periodicity])}
                  />
                );
              })}

            {isEmpty(offers) && (
              <Radiolist items={[]} loading={this.state.loading} />
            )}

            {this.state.loading && <Loader key="loader" mask />}
          </Panel>
        </div>
      </InputBlock>
    );
  }
}

export default OfferRadiolist;
